import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { add, differenceInDays, formatDuration, intervalToDuration } from 'date-fns';
import React, { FormEvent, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { OptionalArraySchema } from 'yup/es/array';
import { OptionalObjectSchema } from 'yup/es/object';
import { IAsset } from '~/models/api/asset';
import { ICreatorStats, ICreatorStatsResponse } from '~/models/api/creator';
import { IAssetDocumentTypes } from '~/models/asset';
import { User } from '~/models/auth';
import api, { useGet } from '~/utils/api';
import { fakeMeta } from '~/utils/helpers/fakeUser';
import urlMetadata from 'url-metadata';

export const formatNumber = (number: number) => {
	if (number > 1000000000) {
		return `${(number / 1000000000).toFixed(2)}B`;
	}

	if (number > 1000000) {
		return `${(number / 1000000).toFixed(2)}M`;
	}

	if (number > 1000) {
		return `${(number / 1000).toFixed(2)}K`;
	}

	return number ?? 0;
};
export const handlePropagationSubmit = (fn: (data: any) => void) => (e: FormEvent) => {
	const { handleSubmit } = useFormContext();
	e.preventDefault();
	e.stopPropagation();

	handleSubmit(fn)(e);
};
export const isCodeEmpty = (code: Code): boolean => {
	for (const key in code) {
		if (code.hasOwnProperty(key) && code[key as keyof Code] !== '') {
			return false; // At least one property has a non-empty value
		}
	}
	return true; // All properties are empty
};

export const FAKE_IMAGE_STORE =
	'https://images.pexels.com/photos/375889/pexels-photo-375889.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2';

export const FAKE_ASSET = {
	sizes: {
		original: {
			url: 'https://images.pexels.com/photos/375889/pexels-photo-375889.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
			fileName: 'https://my-s3/my-org/my-system/id.png',
			size: 8749282,
		},
		large: {
			url: 'https://images.pexels.com/photos/375889/pexels-photo-375889.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
			fileName: 'https://my-s3/my-org/my-system/id.png',
			size: 648492,
		},
		medium: {
			url: 'https://images.pexels.com/photos/375889/pexels-photo-375889.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
			fileName: 'https://my-s3/my-org/my-system/id.png',
			size: 274833,
		},
		small: {
			url: 'https://images.pexels.com/photos/375889/pexels-photo-375889.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
			fileName: 'https://my-s3/my-org/my-system/id.png',
			size: 198722,
		},
		thumbnail: {
			url: 'https://images.pexels.com/photos/375889/pexels-photo-375889.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
			fileName: 'https://my-s3/my-org/my-system/id.png',
			size: 93827,
		},
	},
	documentType: 'image',
	baseName: 'my-file',
	name: 'my-file.png',
	fileName: 'my-file.png',
	extension: '.png',
	type: 'image/png',
	size: 8749282,
	_id: '5435435437',
	internal: true,
} as IAsset;

export const FAKE_EXPERIENCE = {
	meta: {
		created: {
			timestamp: '2023-09-19T17:31:47.183Z',
			userId: '643d04965a92efff4eeff111',
		},
		lastUpdated: {
			timestamp: '2023-09-19T17:31:47.183Z',
			userId: '643d04965a92efff4eeff111',
		},
		admin: [],
		organisationId: '64c38a388f15008d546cde9c',
	},
	_id: '6509db0340c1eac9e735c6cf',
	name: 'The First Experience',
	__v: 0,
	logo: 'https://s3.eu-west-2.amazonaws.com/merlin-cloud/64c38a388f15008d546cde9c/13cbcf0d-5aac-40d0-97df-5eacd74fdb8f.png',
	description: 'This is a description',
	assets: [FAKE_ASSET, FAKE_ASSET],
	marketing: {
		coverImageAsset: FAKE_ASSET,
	},
};

export const kebabCase = (str: string) =>
	str
		.replace(/([a-z])([A-Z])/g, '$1-$2')
		.replace(/[\s_]+/g, '-')
		.toLowerCase();

export function isValidJSON(string: string) {
	return /^[\],:{}\s]*$/.test(
		/* eslint-disable */
		string
			.replace(/\\["\\\/bfnrtu]/g, '@')
			.replace(/"[^"\\\n\r]*"|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?/g, ']')
			.replace(/(?:^|:|,)(?:\s*\[)+/g, '')
		/* eslint-enable */
	);
}

export const getAssetDocumentTypeIcon = (documentType: IAssetDocumentTypes) =>
	documentType === 'document' ? (
		<FontAwesomeIcon size="xs" icon={['fas', 'file-lines']} />
	) : documentType === 'video' ? (
		<FontAwesomeIcon size="xs" icon={['fas', 'file-video']} />
	) : documentType === 'subtitles' ? (
		<FontAwesomeIcon size="xs" icon={['fas', 'closed-captioning']} />
	) : documentType === 'audio' ? (
		<FontAwesomeIcon size="xs" icon={['fas', 'file-audio']} />
	) : (
		<FontAwesomeIcon size="xs" icon={['fas', 'image']} />
	);

export const getDocumentTypeFromFile = (
	file: File & { preview?: string }
): IAssetDocumentTypes | null => {
	const ext = (file.name.split('.').pop() ?? '').toLowerCase();
	return file instanceof File
		? ({
				png: 'image',
				jpg: 'image',
				jpeg: 'image',
				vtt: 'subtitles',
				srt: 'subtitles',
				mov: 'video',
				mp4: 'video',
				mp3: 'audio',
				m4a: 'audio',
				pdf: 'document',
				docx: 'document',
				ppt: 'document',
				pptx: 'document',
		  }[ext] as IAssetDocumentTypes)
		: null;
};

export const formatGainColor = (gain: number) => {
	if (gain > 0) {
		return 'success';
	} else if (gain < 0) {
		return 'danger';
	}

	return 'neutral';
};

export const formatGainsText = (gain: number) => {
	if (gain > 0) {
		return `Up ${gain}`;
	} else if (gain < 0) {
		return `Down ${gain}`;
	}

	return gain;
};

export function isValidStringify(value: string) {
	return (typeof value === 'object' && value !== null) || Array.isArray(value);
}

export const pxToRem = (value: number) => `${value / 16}rem`;

// capitalised the first letter of a sentence, useful for Formik form errors
export const capitalise = (text: string) => {
	return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
};

export const formatTag = (value: string) => {
	return `${value}`
		.split(' ')
		.join('')
		.toLowerCase()
		.replace(/[^\w\s]/gi, '')
		.slice(0, 20);
};

export const isValidLongitude = (longitude?: number) =>
	typeof longitude === 'number' && longitude >= -180 && longitude <= 180;

export const isValidLatitude = (latitude?: number) =>
	typeof latitude === 'number' && latitude >= -90 && latitude <= 90;

export const getUserAvatar = (user: User) => {
	if (!user?.avatar?.id && user?.google?.avatar) {
		return user?.google?.avatar ?? '';
	} else {
		const avatar = `${api.baseURL}${api.accounts.avatar(user?._id ?? '')}?v=${
			user?.avatar?.lastChanged ?? ''
		}`;

		return avatar ?? '';
	}
};

export const minutesToFormat = (
	minutes: string,
	format: string[] = ['years', 'months', 'weeks', 'days', 'hours', 'minutes']
) => {
	const durationDate = intervalToDuration({
		start: Date.now(),
		end: add(new Date(), {
			minutes: Number(minutes) ?? 0,
		}),
	});

	const duration = formatDuration(durationDate, {
		format: format,
	});

	return duration;
};

export const getCreatorStats = (creatorId: string) => {
	const { data: stats, isLoading } = useGet<ICreatorStatsResponse>(
		api.creators.stats(creatorId ?? ''),
		{
			params: { pageNum: 1, perPage: 1000 },
		}
	);

	// Calculate the closest creatorStat to 30 days ago
	const currentDate = new Date();

	const recent = stats?.results?.[stats?.results?.length - 1];
	const old = stats?.results?.filter((stat) => {
		const date = new Date(stat?.meta.created.timestamp ?? '');
		const difference = differenceInDays(currentDate, date);

		if (difference >= 7) {
			//return the most recent stat that is at least 7 days old
			return true;
		}
	})[0];
	let viewGains = 0;
	let subGains = 0;

	if (recent && old) {
		viewGains = ((recent.views - old.views) / old.views) * 100;
		subGains = ((recent.subscribers - old.subscribers) / old.subscribers) * 100;
	}

	const statsResults = {
		mostRecent: recent,
		thirtyDaysAgo: old,
		viewGains: Number(viewGains.toFixed(2)),
		subGains: Number(subGains.toFixed(2)),
		isLoading,
	};

	return statsResults;
};

export const getUserAvatarRaw = (id: string) => {
	return `${api.baseURL}${api.accounts.avatar(id)}?v=${Date.now()}`;
};

export function debounce<T extends (...args: any[]) => void>(
	func: T,
	delay: number
): (...args: Parameters<T>) => void {
	let timeoutId: ReturnType<typeof setTimeout>;
	return function (this: unknown, ...args: Parameters<T>): void {
		const context = this as unknown as T; // Type assertion to maintain function context
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => {
			func.apply(context, args);
		}, delay);
	};
}

// joi specific
export const openSidebar = () => {
	if (typeof document !== 'undefined') {
		document.body.style.overflow = 'hidden';
		document.documentElement.style.setProperty('--SideNavigation-slideIn', '1');
	}
};

export const closeSidebar = () => {
	if (typeof document !== 'undefined') {
		document.documentElement.style.removeProperty('--SideNavigation-slideIn');
		document.body.style.removeProperty('overflow');
	}
};

export const toggleSidebar = () => {
	if (typeof window !== 'undefined' && typeof document !== 'undefined') {
		const slideIn = window
			.getComputedStyle(document.documentElement)
			.getPropertyValue('--SideNavigation-slideIn');
		if (slideIn) {
			closeSidebar();
		} else {
			openSidebar();
		}
	}
};

/**
 * This is a wrapper around the react-hook-form field 'onChange' that supports Joy UI's onChange
 * event having a value as the second argument. (For example, the Select component uses this to pass the
 * real value rather than an index)
 * @param onChange
 */
export const formOnChangeWrapper =
	(onChange: (event: React.SyntheticEvent) => void) =>
	(event: React.SyntheticEvent, value?: any) => {
		if (!value) {
			return onChange(event);
		}

		const target = event.target;
		// make a fake event and simulate data
		const fakeEvent = {
			...event,
			target: {
				...target,
				value,
			},
		};

		return onChange(fakeEvent);
	};

export const altKeyMapping: { [x: string]: number | string } = {
	'¡': 1,
	'€': 2,
	'#': 3,
	'¢': 4,
	'∞': 5,
	'§': 6,
	'¶': 7,
	'•': 8,
	ª: 9,
	ø: 'O',
	Ø: 'O',
};

export const yupResolver = (
	validationSchema: OptionalObjectSchema<any> | OptionalArraySchema<any>
) =>
	useCallback(
		async (data: OptionalObjectSchema<any> | OptionalArraySchema<any>) => {
			try {
				const values = await validationSchema.validate(data, {
					abortEarly: false,
				});

				return {
					values,
					errors: {},
				};
			} catch (errors: any) {
				return {
					values: {},
					errors: errors.inner.reduce(
						(allErrors: any, currentError: any) => ({
							...allErrors,
							[currentError.path]: {
								type: currentError.type ?? 'validation',
								message: currentError.message,
							},
						}),
						{}
					),
				};
			}
		},
		[validationSchema]
	);

export const countryCodes = [
	'af',
	'ax',
	'al',
	'dz',
	'as',
	'ad',
	'ao',
	'ai',
	'aq',
	'ag',
	'ar',
	'am',
	'aw',
	'ac',
	'au',
	'at',
	'az',
	'bs',
	'bh',
	'bd',
	'bb',
	'es-pv',
	'by',
	'be',
	'bz',
	'bj',
	'bm',
	'bt',
	'bo',
	'bq',
	'ba',
	'bw',
	'bv',
	'br',
	'io',
	'bn',
	'bg',
	'bf',
	'bi',
	'cv',
	'kh',
	'cm',
	'ca',
	'ic',
	'es-ct',
	'ky',
	'cf',
	'cefta',
	'td',
	'cl',
	'cn',
	'cx',
	'cp',
	'cc',
	'co',
	'km',
	'ck',
	'cr',
	'hr',
	'cu',
	'cw',
	'cy',
	'cz',
	'ci',
	'cd',
	'dk',
	'dg',
	'dj',
	'dm',
	'do',
	'eac',
	'ec',
	'eg',
	'sv',
	'gb-eng',
	'gq',
	'er',
	'ee',
	'sz',
	'et',
	'eu',
	'fk',
	'fo',
	'fm',
	'fj',
	'fi',
	'fr',
	'gf',
	'pf',
	'tf',
	'ga',
	'es-ga',
	'gm',
	'ge',
	'de',
	'gh',
	'gi',
	'gr',
	'gl',
	'gd',
	'gp',
	'gu',
	'gt',
	'gg',
	'gn',
	'gw',
	'gy',
	'ht',
	'hm',
	'va',
	'hn',
	'hk',
	'hu',
	'is',
	'in',
	'id',
	'ir',
	'iq',
	'ie',
	'im',
	'il',
	'it',
	'jm',
	'jp',
	'je',
	'jo',
	'kz',
	'ke',
	'ki',
	'xk',
	'kw',
	'kg',
	'la',
	'lv',
	'arab',
	'lb',
	'ls',
	'lr',
	'ly',
	'li',
	'lt',
	'lu',
	'mo',
	'mg',
	'mw',
	'my',
	'mv',
	'ml',
	'mt',
	'mh',
	'mq',
	'mr',
	'mu',
	'yt',
	'mx',
	'md',
	'mc',
	'mn',
	'me',
	'ms',
	'ma',
	'mz',
	'mm',
	'na',
	'nr',
	'np',
	'nl',
	'nc',
	'nz',
	'ni',
	'ne',
	'ng',
	'nu',
	'nf',
	'kp',
	'mk',
	'gb-nir',
	'mp',
	'no',
	'om',
	'pk',
	'pw',
	'pa',
	'pg',
	'py',
	'pe',
	'ph',
	'pn',
	'pl',
	'pt',
	'pr',
	'qa',
	'cg',
	'ro',
	'ru',
	'rw',
	're',
	'bl',
	'sh',
	'kn',
	'lc',
	'mf',
	'pm',
	'vc',
	'ws',
	'sm',
	'st',
	'sa',
	'gb-sct',
	'sn',
	'rs',
	'sc',
	'sl',
	'sg',
	'sx',
	'sk',
	'si',
	'sb',
	'so',
	'za',
	'gs',
	'kr',
	'ss',
	'es',
	'lk',
	'ps',
	'sd',
	'sr',
	'sj',
	'se',
	'ch',
	'sy',
	'tw',
	'tj',
	'tz',
	'th',
	'tl',
	'tg',
	'tk',
	'to',
	'tt',
	'ta',
	'tn',
	'tm',
	'tc',
	'tv',
	'tr',
	'ug',
	'ua',
	'ae',
	'gb',
	'un',
	'um',
	'us',
	'xx',
	'uy',
	'uz',
	'vu',
	've',
	'vn',
	'vg',
	'vi',
	'gb-wls',
	'wf',
	'eh',
	'ye',
	'zm',
	'zw',
] as const;

export const loremIpsum = (length: number) => {
	return Array(length)
		.fill(0)
		.map(() => 'lorem ipsum dolor sit amet')
		.join(' ');
};
